import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  forwardRef,
  OnInit
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
  FormControl
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { calendarlogoComponent } from "../icons/calendar";

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, calendarlogoComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-2 relative" #dateContainer>
      <label *ngIf="label" class="text-[#616161]">{{ label }}</label>

      <div class="flex relative">
        <input
          [value]="displayValue"
          (click)="togglePicker()"
          [placeholder]="placeholder"
          readonly
          [disabled]="disabled"
          class="p-3 rounded-lg border w-[100%] border-[#CED4DA] focus:outline-none"
          [class.cursor-pointer]="!disabled"
          [class.cursor-not-allowed]="disabled"
          [class.bg-gray-100]="disabled"
        />

        <div
          class="absolute right-3 -z-10"
          [ngClass]="{ 'top-[45px]': label, 'top-[12px]': !label }"
        >
          <app-calendar-logo></app-calendar-logo>
        </div>
      </div>

      <div
        *ngIf="showPicker && !disabled"
        class="absolute top-full left-0 mt-2 z-10 bg-white border border-gray-200 shadow-lg rounded-lg p-4 w-[294px]"
      >
        <div class="flex justify-between items-center mb-4">
          <button type="button" (click)="navigateMonth(-1)">&#8592;</button>
          <span class="font-semibold">{{ getMonthYearHeader() }}</span>
          <button type="button" (click)="navigateMonth(1)">&#8594;</button>
        </div>

        <div class="grid grid-cols-7 gap-1 mb-2">
          <div
            *ngFor="let day of dayHeaders"
            class="text-center text-sm font-medium text-[#616161]"
          >
            {{ day }}
          </div>
        </div>

        <div class="grid grid-cols-7 gap-1">
          <div
            *ngFor="let day of calendarDays"
            class="text-center p-2 rounded-full transition text-sm"
            [class.bg-[#043B22]]="isSelected(day.date)"
            [class.text-white]="isSelected(day.date)"
            [class.text-[#b7b7b7]]="!isCurrentMonth(day.date)"
            [class.cursor-not-allowed]="isDisabled(day.date)"
            [class.cursor-pointer]="!isDisabled(day.date)"
            [class.hover:bg-gray-100]="
              !isSelected(day.date) &&
              isCurrentMonth(day.date) &&
              !isDisabled(day.date)
            "
            (click)="!isDisabled(day.date) && selectDate(day.date)"
          >
            {{ day.day }}
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      input {
        min-width: 200px;
      }
    `
  ]
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  @ViewChild('dateContainer') containerRef!: ElementRef;
  @Input() label: string = '';
  @Input() placeholder: string = 'Select date';
  @Input() minDate: any | null = null;
  @Input() maxDate: any | null = null;
  @Input() disabled: boolean = false;
  @Output() dateChange = new EventEmitter<Date | null>();

  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() isEndDatePicker: boolean = false;

  private _control?: FormControl;

  @Input() set control(control: FormControl | undefined) {
    this._control = control;
    if (control && control.value) {
      this.writeValue(control.value);
    }
  }

  showPicker = false;
  displayValue = '';
  currentViewDate = new Date();
  dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  private _value: Date | null = null;
  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit() {
    if (this._control) {
      this._control.valueChanges.subscribe(value => {
        if (value !== this._value) {
          this.writeValue(value);
        }
      });
    }

    if (this._value) {
      this.currentViewDate = new Date(this._value);
    }
  }

  get value(): Date | null {
    return this._value;
  }

  set value(val: Date | null) {
    const newDate = val ? new Date(val) : null;
    if (!this.datesEqual(this._value, newDate)) {
      this._value = newDate;
      this.updateDisplayValue();
      this.onChange(newDate);
      this.onTouched();
      this.dateChange.emit(newDate);

      if (this._control && this._control.value !== newDate) {
        this._control.setValue(newDate, { emitEvent: false });
        this._control.markAsTouched();
      }
    }
  }

  togglePicker() {
    if (this.disabled) return;
    this.showPicker = !this.showPicker;
    if (this.showPicker && !this._value) {
      this.currentViewDate = new Date();
    } else if (this.showPicker && this._value) {
      this.currentViewDate = new Date(this._value);
    }
  }

  navigateMonth(delta: number) {
    this.currentViewDate = new Date(
      this.currentViewDate.getFullYear(),
      this.currentViewDate.getMonth() + delta,
      1
    );
  }

  getMonthYearHeader(): string {
    return this.currentViewDate.toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
  }

  get calendarDays(): { day: number; date: Date }[] {
    const year = this.currentViewDate.getFullYear();
    const month = this.currentViewDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = firstDay.getDay();
    const totalDaysToShow = Math.ceil((daysInMonth + prevMonthDays) / 7) * 7;
    const nextMonthDays = totalDaysToShow - (daysInMonth + prevMonthDays);

    const days = [];

    // Add previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({ day: date.getDate(), date });
    }

    // Add current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({ day: date.getDate(), date });
    }

    // Add next month days
    for (let i = 1; i <= nextMonthDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ day: date.getDate(), date });
    }

    return days;
  }

  isSelected(date: Date): boolean {
    if (!this.value) return false;
    return (
      date.getDate() === this.value.getDate() &&
      date.getMonth() === this.value.getMonth() &&
      date.getFullYear() === this.value.getFullYear()
    );
  }

  isCurrentMonth(date: Date): boolean {
    return (
      date.getMonth() === this.currentViewDate.getMonth() &&
      date.getFullYear() === this.currentViewDate.getFullYear()
    );
  }

  isDisabled(date: Date): boolean {
    if (this.disabled) return true;

    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    // Check min/max date constraints
    if (this.minDate) {
      const min = new Date(this.minDate);
      min.setHours(0, 0, 0, 0);
      if (normalizedDate < min) return true;
    }

    if (this.maxDate) {
      const max = new Date(this.maxDate);
      max.setHours(23, 59, 59, 999);
      if (normalizedDate > max) return true;
    }

    // For end date picker: disable dates before start date
    if (this.isEndDatePicker && this.startDate) {
      const start = new Date(this.startDate);
      start.setHours(0, 0, 0, 0);
      return normalizedDate < start;
    }

    // For start date picker: disable dates after end date
    if (!this.isEndDatePicker && this.endDate) {
      const end = new Date(this.endDate);
      end.setHours(23, 59, 59, 999);
      return normalizedDate > end;
    }

    return false;
  }

  selectDate(date: Date) {
    if (this.disabled || this.isDisabled(date)) return;

    if (!this.isCurrentMonth(date)) {
      this.currentViewDate = new Date(date.getFullYear(), date.getMonth(), 1);
      return;
    }

    this.value = new Date(date);
    this.showPicker = false;
  }

  updateDisplayValue() {
    if (this.value) {
      this.displayValue = this.value.toLocaleDateString('default', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else {
      this.displayValue = '';
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.containerRef?.nativeElement.contains(event.target)) {
      this.showPicker = false;
    }
  }

  writeValue(obj: Date | string | null): void {
    if (obj) {
      const newDate = typeof obj === 'string' ? new Date(obj) : new Date(obj);
      if (isNaN(newDate.getTime())) {
        this._value = null;
      } else {
        this._value = newDate;
        this.currentViewDate = new Date(this._value);
      }
    } else {
      this._value = null;
    }
    this.updateDisplayValue();
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private datesEqual(date1: Date | null, date2: Date | null): boolean {
    if (date1 === date2) return true;
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
}
